import React, { useEffect, useState } from "react";
import { Spin, DatePicker, Typography, Space } from "antd";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import ExcelExportButton from "./ExcelExportButton";

const { Title } = Typography;

const YearlySalesChart: React.FC = () => {
    const { fetchYearlySalesChart } = useReportApi();
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        fetchYearlySalesChart({ year }).then(res => {
            setData(
                Array.from({ length: 12 }, (_, i) => {
                    const found = res.find((d: any) => d._id === i + 1);
                    return { month: i + 1, total: found ? found.total : 0 };
                })
            );
            setLoading(false);
        });
    }, [year, fetchYearlySalesChart]);

    return (
        <>
            <Title level={4} style={{ marginBottom: 12 }}>{t("yearly_sales_chart")}</Title>
            <Space style={{ marginBottom: 8 }} wrap>
                <DatePicker
                    picker="year"
                    value={dayjs(`${year}-01-01`)}
                    onChange={d => setYear(d ? d.year() : currentYear)}
                    placeholder={t("select_year") as string}
                />
                <ExcelExportButton
                    fileName={`yearly-sales-${year}`}
                    sheetName={t("yearly_sales_chart") as string}
                    columns={[
                        { header: t("month") as string, key: (row: any) => t(`month_${row.month}`) || row.month, width: 18 },
                        { header: t("total_sales") as string, key: "total", width: 18 },
                    ]}
                    rows={data}
                    footer={[[t("total") as string, data.reduce((sum, row) => sum + (row.total || 0), 0)]]}
                    title={[`${t("yearly_sales_chart")} — ${year}`]}
                />
            </Space>
            <Spin spinning={loading}>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="month"
                            tickFormatter={(m: number) => t("month_" + m) || String(m)}
                        />
                        <YAxis />
                        <Tooltip
                            formatter={(value: any) => [value, t("total_sales")]}
                            labelFormatter={(label: any) => t("month_" + label) || label}
                        />
                        <Line type="monotone" dataKey="total" stroke="#22314A" />
                    </LineChart>
                </ResponsiveContainer>
            </Spin>
        </>
    );
};

export default YearlySalesChart;